import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    adminApi,
    CreateCandidateParams,
    CreateCandidateResponse,
    CandidatesResponse,
    RatingsResponse,
    hasValidSession,
    setSessionToken,
} from '../api';

interface AdminContextValue {
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (user: string, pass: string) => Promise<boolean>;
    loginWithIdToken: (idToken: string) => Promise<boolean>;
    logout: () => Promise<void>;
    fetchRatings: () => Promise<RatingsResponse>;
    fetchCandidates: () => Promise<CandidatesResponse>;
    createCandidate: (params: CreateCandidateParams) => Promise<CreateCandidateResponse>;
}

const AdminContext = createContext<AdminContextValue | null>(null);

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Initialize from stored session to prevent flash of login screen
    const [isAuthenticated, setIsAuthenticated] = useState(() => hasValidSession());
    const [isLoading, setIsLoading] = useState(true);

    // Verify authentication status on mount by trying to fetch data
    useEffect(() => {
        const checkAuth = async () => {
            // If no stored session, skip the API check
            if (!hasValidSession()) {
                setIsAuthenticated(false);
                setIsLoading(false);
                return;
            }

            try {
                // Verify the session is still valid on the server
                const result = await adminApi.getCandidates();
                if (result.ok) {
                    setIsAuthenticated(true);
                } else {
                    // Server rejected the session, clear it
                    setSessionToken(null);
                    setIsAuthenticated(false);
                }
            } catch {
                // Session invalid or network error, clear stored session
                setSessionToken(null);
                setIsAuthenticated(false);
            } finally {
                setIsLoading(false);
            }
        };
        checkAuth();
    }, []);

    const login = async (user: string, pass: string): Promise<boolean> => {
        try {
            await adminApi.login(user, pass);
            setIsAuthenticated(true);
            return true;
        } catch (error) {
            console.error('Login failed:', error);
            return false;
        }
    };

    const loginWithIdToken = async (idToken: string): Promise<boolean> => {
        try {
            const result = await adminApi.firebaseLogin(idToken);
            if (result.ok) {
                setIsAuthenticated(true);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Firebase login failed:', error);
            return false;
        }
    };

    const logout = async (): Promise<void> => {
        try {
            await adminApi.logout();
        } catch {
            // ignore
        }
        // Clear the stored session token
        setSessionToken(null);
        setIsAuthenticated(false);
    };

    const fetchRatings = async (): Promise<RatingsResponse> => {
        try {
            return await adminApi.getRatings();
        } catch (error) {
            console.error('fetchRatings failed:', error);
            return { ok: false, items: [], totals: { total: 0, rated: 0, feedback: 0 } };
        }
    };

    const fetchCandidates = async (): Promise<CandidatesResponse> => {
        try {
            return await adminApi.getCandidates();
        } catch (error) {
            console.error('fetchCandidates failed:', error);
            return { ok: false, candidates: [] };
        }
    };

    const createCandidate = async (params: CreateCandidateParams): Promise<CreateCandidateResponse> => {
        try {
            return await adminApi.createCandidate(params);
        } catch (e: any) {
            return { ok: false, error: e.message || 'Failed to create candidate' };
        }
    };

    return (
        <AdminContext.Provider
            value={{
                isAuthenticated,
                isLoading,
                login,
                loginWithIdToken,
                logout,
                fetchRatings,
                fetchCandidates,
                createCandidate,
            }}
        >
            {children}
        </AdminContext.Provider>
    );
};

export function useAdmin() {
    const ctx = useContext(AdminContext);
    if (!ctx) throw new Error('useAdmin must be used within AdminProvider');
    return ctx;
}
